import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Edit3, RefreshCw, Save, X } from 'lucide-react';
import type { CreditBlockProduct, BureauCredential } from '../../types';
import type { ListViewRecord } from '../../types/listView';
import { StandardSelect } from '../../components/standard';
import SalesforceRelatedList from '../../components/credit-block/SalesforceRelatedList';
import type { RelatedListColumn } from '../../components/credit-block/SalesforceRelatedList';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { getCustomSuffix } from '../../config/gobodoConfig';
import { getPicklistValues, prefetchFieldMetadata } from '../../services/sfFieldMetadataService';
import {
  buildBureauRelatedRows,
  type BureauRelatedRow,
  type BureauToolbarAction,
} from './creditBlockClientConstants';
import { BUREAU_RELATED_COLUMNS } from './creditBlockClientBureauColumns';
import {
  formatSubDate,
  normalizeDateForInput,
  subscriptionDisplayLabel,
  subscriptionStageLabel,
} from './creditBlockSubscriptionDisplay';
import { formatAuditUserDate } from '../../utils/creditBlockFormatters';

/** Bureau actions toolbar — matches SF toolbar: gear, refresh, First Time Block, Unblock Bureau dropdown */
export const BureauActionsToolbar: React.FC<{
  onAction: (kind: BureauToolbarAction) => void;
  onRefresh?: () => void;
  canStartFirstTimeBlock?: boolean;
}> = ({ onAction, onRefresh, canStartFirstTimeBlock = false }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="flex items-center justify-between">
      <div />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onRefresh}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={!canStartFirstTimeBlock}
          onClick={() => canStartFirstTimeBlock && onAction('first_time_block')}
          className={`px-3 py-1.5 text-xs font-medium border rounded ${
            canStartFirstTimeBlock
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          }`}
          title={canStartFirstTimeBlock ? 'Start First Time Block' : 'You do not have permission to start this flow'}
        >
          First Time Block
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen((o) => !o)}
            className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 inline-flex items-center gap-1"
          >
            Unblock Bureau
            <ChevronDown className="w-3 h-3" />
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20 py-1">
              {[
                { kind: 'unlock_request' as const, label: 'Unblock Bureaus' },
                { kind: 'extension' as const, label: 'Unblock Extension' },
                { kind: 'permanent_unblock' as const, label: 'Permanent Unblock' },
                { kind: 'block_bureaus' as const, label: 'Block Bureaus' },
              ].map(({ kind, label }) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => {
                    onAction(kind);
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CreditBlockSubscriptionWorkspaceTabContent: React.FC<{
  accountName: string;
  subscriptionRows: CreditBlockProduct[];
  subscriptionsLoading: boolean;
  selectedSubscriptionId: string | null;
  onSelectSubscriptionId: (id: string | null) => void;
  bureauCredentials: Record<string, BureauCredential>;
  bureausLoading: boolean;
  creditBlockCases: ListViewRecord[];
  casesLoading: boolean;
  onBureauAction: (kind: BureauToolbarAction) => void;
  onSettings?: () => void;
  onRefresh?: () => void;
  onBureauClick?: (bureauKey: string) => void;
  onCaseClick?: (caseRecord: ListViewRecord) => void;
  onUpdateSubscriptionField?: (subscription: CreditBlockProduct, updates: Record<string, unknown>) => Promise<void>;
  isUpdatingSubscription?: boolean;
  canEditCreditBlock?: boolean;
  canStartFirstTimeBlock?: boolean;
}> = ({
  accountName,
  subscriptionRows,
  subscriptionsLoading,
  selectedSubscriptionId,
  onSelectSubscriptionId,
  bureauCredentials,
  bureausLoading,
  creditBlockCases,
  casesLoading,
  onBureauAction,
  onSettings,
  onRefresh,
  onBureauClick,
  onCaseClick,
  onUpdateSubscriptionField,
  isUpdatingSubscription = false,
  canEditCreditBlock = false,
  canStartFirstTimeBlock = false,
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [draftEdits, setDraftEdits] = useState<Record<string, string>>({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalValues, setEditModalValues] = useState<Record<string, string>>({});
  const [editModalSaving, setEditModalSaving] = useState(false);
  const [metaVersion, setMetaVersion] = useState(0);

  useEffect(() => {
    let mounted = true;
    prefetchFieldMetadata('CreditBlock__c')
      .catch(() => null)
      .finally(() => {
        if (mounted) setMetaVersion((v) => v + 1);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const effectiveSub = useMemo(() => {
    if (!subscriptionRows.length) return null;
    const byId = selectedSubscriptionId ? subscriptionRows.find((s) => s.id === selectedSubscriptionId) : null;
    return byId || subscriptionRows[0];
  }, [subscriptionRows, selectedSubscriptionId]);

  useEffect(() => {
    setEditingField(null);
    setEditValue('');
    setDraftEdits({});
  }, [effectiveSub?.id]);

  const bureauRows = useMemo(() => buildBureauRelatedRows(bureauCredentials), [bureauCredentials]);
  const bureauColumnsWithClick = useMemo<RelatedListColumn<BureauRelatedRow>[]>(() => {
    if (!onBureauClick) return BUREAU_RELATED_COLUMNS;
    return BUREAU_RELATED_COLUMNS.map((col) =>
      col.key === 'bureauName'
        ? { ...col, isLink: true, onLinkClick: (row: BureauRelatedRow) => onBureauClick(row.bureauName.toLowerCase()) }
        : col
    );
  }, [onBureauClick]);
  const openCases = useMemo(
    () =>
      creditBlockCases.filter((c) => {
        const st = String(c.status || '').toLowerCase();
        return st && !['closed', 'cancelled', 'canceled'].includes(st);
      }),
    [creditBlockCases]
  );
  const openCasesCountLabel = casesLoading ? '—' : String(openCases.length);
  const stageOptions = useMemo(() => {
    void metaVersion;
    return getPicklistValues('CreditBlock__c', 'Stage__c') ?? [];
  }, [metaVersion]);
  const creditProgramTypeOptions = useMemo(() => {
    void metaVersion;
    return getPicklistValues('CreditBlock__c', 'Credit_Program_Type__c') ?? [];
  }, [metaVersion]);
  const optOutStatusOptions = useMemo(() => {
    void metaVersion;
    return getPicklistValues('CreditBlock__c', 'Opt_out_Prescreen_Status__c') ?? [];
  }, [metaVersion]);

  if (subscriptionsLoading && !subscriptionRows.length) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mr-3" />
        Loading subscriptions…
      </div>
    );
  }

  if (!subscriptionRows.length) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 py-8">
        No Credit Block subscription rows for this client. Create or sync a subscription in Gobodo first.
      </p>
    );
  }

  const sub = effectiveSub || subscriptionRows[0];
  const canEdit = !!onUpdateSubscriptionField && !isUpdatingSubscription && canEditCreditBlock;
  const subRecord = sub as unknown as Record<string, unknown>;
  const firstNonEmptyText = (...values: unknown[]): string => {
    for (const v of values) {
      const s = String(v ?? '').trim();
      if (s) return s;
    }
    return '—';
  };
  const createdByLabel = firstNonEmptyText(
    subRecord.createdByName,
    subRecord.CreatedByName,
    subRecord.createdBy,
    subRecord.CreatedBy__c,
    subRecord.CreatedBy
  );
  const lastModifiedByLabel = firstNonEmptyText(
    subRecord.lastModifiedByName,
    subRecord.LastModifiedByName,
    subRecord.lastModifiedBy,
    subRecord.LastModifiedBy__c,
    subRecord.LastModifiedBy
  );
  const createdDateRaw = firstNonEmptyText(
    sub.createdDate,
    subRecord.CreatedDate__c,
    subRecord.CreatedDate,
    subRecord.createdDate,
    subRecord.created_date
  );
  const lastModifiedDateRaw = firstNonEmptyText(
    sub.lastModifiedDate,
    subRecord.LastModifiedDate__c,
    subRecord.LastModifiedDate,
    subRecord.lastModifiedDate,
    subRecord.last_modified_date
  );
  const createdAuditLabel = formatAuditUserDate(createdByLabel === '—' ? '' : createdByLabel, createdDateRaw === '—' ? '' : createdDateRaw);
  const lastModifiedAuditLabel = formatAuditUserDate(
    lastModifiedByLabel === '—' ? '' : lastModifiedByLabel,
    lastModifiedDateRaw === '—' ? '' : lastModifiedDateRaw
  );
  const startEdit = (fieldKey: string, value: string) => {
    if (editingField && editingField !== fieldKey) {
      setDraftEdits((prev) => ({ ...prev, [editingField]: editValue ?? '' }));
    }
    setEditingField(fieldKey);
    setEditValue(draftEdits[fieldKey] ?? value ?? '');
  };
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };
  const saveEdit = async (fieldKey: string) => {
    if (!onUpdateSubscriptionField) return;
    const fieldApiMap: Record<string, string> = {
      stage: 'Stage__c',
      creditProgramType: 'Credit_Program_Type__c',
      startDate: 'Start_Date__c',
      endDate: 'End_Date__c',
      initialLockdownDate: 'Initial_Lockdown_Date__c',
      optOutPrescreenStartDate: 'Opt_out_Prescreen_Start_Date__c',
      optOutPrescreenStatus: 'Opt_out_Prescreen_Status__c',
    };
    const stagedDrafts = { ...draftEdits, [fieldKey]: editValue ?? '' };
    const fieldSuffix = getCustomSuffix();
    const updates: Record<string, unknown> = {};
    for (const [draftKey, draftValue] of Object.entries(stagedDrafts)) {
      const apiField = fieldApiMap[draftKey];
      if (!apiField) continue;
      const value = draftValue || null;
      updates[apiField] = value;
      if (fieldSuffix && apiField.endsWith('__c')) {
        const suffixedApiField = apiField.replace(/__c$/i, fieldSuffix);
        updates[suffixedApiField] = value;
      }
    }
    if (!Object.keys(updates).length) return;
    await onUpdateSubscriptionField(sub, updates);
    setDraftEdits({});
    setEditingField(null);
    setEditValue('');
  };

  const openEditModal = () => {
    setEditModalValues({
      stage: subscriptionStageLabel(sub),
      creditProgramType: sub.creditProgramType ?? '',
      startDate: normalizeDateForInput(sub.enrollmentDate),
      endDate: normalizeDateForInput(sub.endDate),
      initialLockdownDate: normalizeDateForInput(sub.initialLockdownDate),
      optOutPrescreenStartDate: normalizeDateForInput(sub.optOutPrescreenStartDate),
      optOutPrescreenStatus: sub.optOutPrescreenStatus ?? '',
    });
    setEditModalOpen(true);
  };

  const saveEditModal = async () => {
    if (!onUpdateSubscriptionField) return;
    setEditModalSaving(true);
    try {
      const fieldApiMap: Record<string, string> = {
        stage: 'Stage__c',
        creditProgramType: 'Credit_Program_Type__c',
        startDate: 'Start_Date__c',
        endDate: 'End_Date__c',
        initialLockdownDate: 'Initial_Lockdown_Date__c',
        optOutPrescreenStartDate: 'Opt_out_Prescreen_Start_Date__c',
        optOutPrescreenStatus: 'Opt_out_Prescreen_Status__c',
      };
      const fieldSuffix = getCustomSuffix();
      const updates: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(editModalValues)) {
        const apiField = fieldApiMap[key];
        if (!apiField) continue;
        updates[apiField] = value || null;
        if (fieldSuffix && apiField.endsWith('__c')) {
          updates[apiField.replace(/__c$/i, fieldSuffix)] = value || null;
        }
      }
      if (Object.keys(updates).length > 0) {
        await onUpdateSubscriptionField(sub, updates);
      }
      setEditModalOpen(false);
      setDraftEdits({});
    } finally {
      setEditModalSaving(false);
    }
  };
  const renderEditableField = (
    fieldKey: string,
    label: string,
    rawValue: string,
    inputType: 'text' | 'date' | 'select' = 'text',
    selectOptions: string[] = []
  ) => {
    const isEditing = editingField === fieldKey;
    const value = rawValue ?? '';
    const draftValue = editingField === fieldKey ? editValue : draftEdits[fieldKey] ?? value;
    const resolvedSelectOptions = Array.from(new Set([...(selectOptions || []), draftValue].filter(Boolean)));
    return (
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className="mt-0.5 flex items-center gap-2 min-h-[24px]">
          {isEditing ? (
            <>
              {inputType === 'select' ? (
                <select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-8 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="">—</option>
                  {resolvedSelectOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={inputType}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-8 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
                />
              )}
              <button
                type="button"
                onClick={() => saveEdit(fieldKey)}
                className="p-1 text-emerald-600 hover:text-emerald-700 disabled:text-gray-400"
                disabled={isUpdatingSubscription}
                title="Save"
              >
                <Save className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-400"
                disabled={isUpdatingSubscription}
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <span className="text-gray-900 dark:text-gray-100">
                {inputType === 'date' ? formatSubDate(draftValue) : draftValue || '—'}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => startEdit(fieldKey, draftValue)}
                  className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  title={`Edit ${label}`}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{subscriptionDisplayLabel(sub)}</h2>
        {subscriptionRows.length > 1 ? (
          <StandardSelect
            value={sub.id}
            onChange={(e) => onSelectSubscriptionId(e.target.value)}
            options={subscriptionRows.map((r) => ({
              value: r.id,
              label: `${subscriptionDisplayLabel(r)} — ${subscriptionStageLabel(r)}`,
            }))}
          />
        ) : null}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Subscription Details</h3>
          {canEdit && (
            <button
              type="button"
              onClick={openEditModal}
              className="px-3 py-1 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1"
            >
              <Edit3 className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
        <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Subscription ID</div>
            <div className="mt-0.5 font-mono text-gray-900 dark:text-gray-100">{subscriptionDisplayLabel(sub)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Subscriber</div>
            <div className="mt-0.5 text-blue-600 dark:text-blue-400">{accountName}</div>
          </div>
          {renderEditableField('stage', 'Stage', subscriptionStageLabel(sub), 'select', stageOptions)}
          {renderEditableField('creditProgramType', 'Credit Program Type', sub.creditProgramType ?? '', 'select', creditProgramTypeOptions)}
          {renderEditableField('startDate', 'Start Date', sub.enrollmentDate ?? '', 'date')}
          {renderEditableField('endDate', 'End Date', sub.endDate ?? '', 'date')}
          {renderEditableField('initialLockdownDate', 'Initial Lockdown', sub.initialLockdownDate ?? '', 'date')}
          {renderEditableField('optOutPrescreenStartDate', 'Opt-out Prescreen Start', sub.optOutPrescreenStartDate ?? '', 'date')}
          {renderEditableField('optOutPrescreenStatus', 'Opt-out Prescreen Status', sub.optOutPrescreenStatus ?? '', 'select', optOutStatusOptions)}
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Open Cases</div>
            <div className="mt-0.5 text-gray-900 dark:text-gray-100 tabular-nums">{openCasesCountLabel}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Created By</div>
            <div className="mt-0.5 text-gray-900 dark:text-gray-100">
              {createdAuditLabel || '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Last Modified By</div>
            <div className="mt-0.5 text-gray-900 dark:text-gray-100">
              {lastModifiedAuditLabel || '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Activity</h3>
        </div>
        <div className="px-4 py-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No activities to show. Get started by sending an email, scheduling a task, and more.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
            Open Credit Block Cases ({casesLoading ? '…' : openCases.length})
          </h3>
        </div>
        {casesLoading ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
        ) : openCases.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">No open cases.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Case #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subject</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {openCases.slice(0, 10).map((c) => {
                  const caseNum = String(c.caseNumber ?? (c as Record<string, unknown>).numericObjectId ?? '').trim();
                  const st = String(c.status ?? '').trim();
                  const pr = String(c.priority ?? '').trim();
                  const ow = String(c.owner ?? '').trim();
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => onCaseClick?.(c)}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 font-medium">{caseNum || '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 truncate max-w-[250px]">{c.subject || '—'}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            st === 'New'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : st === 'In Progress' || st === 'Assigned'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : st === 'Escalated'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {st || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            pr === 'High' || pr === 'Critical'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : pr === 'Medium'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}
                        >
                          {pr || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{ow || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => !editModalSaving && setEditModalOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit Subscription</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">{subscriptionDisplayLabel(sub)}</span>
            </div>
            <div className={`px-6 py-5 space-y-4 ${editModalSaving ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Stage</label>
                  <select
                    value={editModalValues.stage ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, stage: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">—</option>
                    {stageOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Credit Program Type</label>
                  <select
                    value={editModalValues.creditProgramType ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, creditProgramType: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">—</option>
                    {creditProgramTypeOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={editModalValues.startDate ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, startDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={editModalValues.endDate ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, endDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Initial Lockdown</label>
                  <input
                    type="date"
                    value={editModalValues.initialLockdownDate ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, initialLockdownDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Opt-out Prescreen Start</label>
                  <input
                    type="date"
                    value={editModalValues.optOutPrescreenStartDate ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, optOutPrescreenStartDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Opt-out Prescreen Status</label>
                  <select
                    value={editModalValues.optOutPrescreenStatus ?? ''}
                    onChange={(e) => setEditModalValues((p) => ({ ...p, optOutPrescreenStatus: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">—</option>
                    {optOutStatusOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                disabled={editModalSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditModal}
                disabled={editModalSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {editModalSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BureauActionsToolbar onAction={onBureauAction} onRefresh={onRefresh} canStartFirstTimeBlock={canStartFirstTimeBlock} />

      <SalesforceRelatedList<BureauRelatedRow>
        title="Credit Bureaus"
        rows={bureauRows}
        columns={bureauColumnsWithClick}
        pageSize={10}
        loading={USE_GOBODO_API && bureausLoading}
        rowKey={(r) => r.id}
        onSettings={onSettings}
        onRefresh={onRefresh}
      />
    </div>
  );
};
